(function () {
  const GSAP_URL =
    'https://cdn.jsdelivr.net/npm/gsap@3.15/dist/gsap.min.js';

  const SCROLL_TRIGGER_URL =
    'https://cdn.jsdelivr.net/npm/gsap@3.15/dist/ScrollTrigger.min.js';

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        `script[src="${src}"]`
      );

      if (existingScript) {
        if (existingScript.dataset.loaded === 'true') {
          resolve();
          return;
        }

        existingScript.addEventListener('load', resolve, {
          once: true
        });

        existingScript.addEventListener('error', reject, {
          once: true
        });

        return;
      }

      const script = document.createElement('script');

      script.src = src;
      script.async = true;

      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );

      script.addEventListener('error', reject, {
        once: true
      });

      document.head.appendChild(script);
    });
  }

  async function loadDependencies() {
    if (typeof window.gsap === 'undefined') {
      await loadScript(GSAP_URL);
    }

    if (typeof window.ScrollTrigger === 'undefined') {
      await loadScript(SCROLL_TRIGGER_URL);
    }

    if (
      typeof window.gsap !== 'undefined' &&
      typeof window.ScrollTrigger !== 'undefined'
    ) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }
  }

  function slugify(text, slugCounts) {
    let slug = text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    if (!slug) {
      slug = 'seccion';
    }

    const currentCount = slugCounts.get(slug) || 0;

    slugCounts.set(slug, currentCount + 1);

    if (currentCount === 0) {
      return slug;
    }

    return `${slug}-${currentCount + 1}`;
  }

  function stripSkipMarker(element, marker) {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT
    );

    let textNode;

    while ((textNode = walker.nextNode())) {
      if (textNode.textContent.includes(marker)) {
        textNode.textContent = textNode.textContent
          .replace(marker, '')
          .trim();
      }
    }
  }

  function destroyTableOfContents(root) {
    const triggers = root._altapetsTocTriggers || [];

    triggers.forEach((trigger) => {
      trigger.kill();
    });

    root._altapetsTocTriggers = [];
    root.dataset.tocInitialized = 'false';
  }

  function initializeTableOfContents(root) {
    if (
      !root ||
      root.dataset.tocInitialized === 'true' ||
      typeof window.ScrollTrigger === 'undefined'
    ) {
      return;
    }

    const contentElement = root.querySelector(
      '[data-toc-content]'
    );

    const listElement = root.querySelector('[data-toc-list]');

    const templateLink = listElement
      ? listElement.querySelector('[data-toc-link]')
      : null;

    const sidebar = root.querySelector('.toc-sidebar');

    if (
      !contentElement ||
      !listElement ||
      !templateLink
    ) {
      return;
    }

    const configuredLevels =
      root.getAttribute('data-toc-levels') || 'h2,h3';

    const headingLevels = configuredLevels
      .split(',')
      .map((level) => level.trim().toLowerCase())
      .filter((level) => /^h[1-6]$/.test(level));

    const headingSelector = headingLevels.join(', ');

    if (!headingSelector) {
      return;
    }

    const offset =
      parseInt(
        root.getAttribute('data-toc-offset'),
        10
      ) || 80;

    const skipMarker = '{skip}';
    const slugCounts = new Map();

    const allHeadings = Array.from(
      contentElement.querySelectorAll(headingSelector)
    );

    const headings = [];

    allHeadings.forEach((heading) => {
      if (heading.hasAttribute('data-toc-ignore')) {
        return;
      }

      if (heading.textContent.includes(skipMarker)) {
        stripSkipMarker(heading, skipMarker);
        return;
      }

      const headingText = heading.textContent.trim();

      if (!headingText) {
        return;
      }

      headings.push(heading);
    });

    if (!headings.length) {
      if (sidebar) {
        sidebar.hidden = true;
      }

      root.classList.add('toc-layout--without-sidebar');
      templateLink.remove();
      root.dataset.tocInitialized = 'true';

      return;
    }

    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = slugify(
          heading.textContent.trim(),
          slugCounts
        );
      }
    });

    const tocLinks = [];

    headings.forEach((heading) => {
      const clonedLink = templateLink.cloneNode(true);

      const textTarget =
        clonedLink.querySelector('[data-toc-text]') ||
        clonedLink;

      textTarget.textContent = heading.textContent.trim();

      clonedLink.href = `#${heading.id}`;
      clonedLink.removeAttribute('data-toc-link');
      clonedLink.setAttribute('data-toc-item', '');

      const headingDepth = heading.tagName.charAt(1);

      clonedLink.setAttribute(
        'data-toc-depth',
        headingDepth
      );

      listElement.appendChild(clonedLink);
      tocLinks.push(clonedLink);
    });

    templateLink.remove();

    function setActiveLink(activeIndex) {
      tocLinks.forEach((link, index) => {
        if (index === activeIndex) {
          link.setAttribute(
            'data-toc-status',
            'active'
          );
        } else {
          link.removeAttribute('data-toc-status');
        }
      });
    }

    const triggers = headings.map(
      (heading, headingIndex) => {
        const nextHeading = headings[headingIndex + 1];

        return window.ScrollTrigger.create({
          trigger: heading,
          start: `top ${offset + 1}px`,
          endTrigger: nextHeading || contentElement,
          end: nextHeading
            ? `top ${offset + 1}px`
            : 'bottom top',
          onToggle: (self) => {
            if (self.isActive) {
              setActiveLink(headingIndex);
            }
          }
        });
      }
    );

    root._altapetsTocTriggers = triggers;

    setActiveLink(0);

    listElement.addEventListener('click', (event) => {
      const link = event.target.closest(
        '[data-toc-item]'
      );

      if (!link || !listElement.contains(link)) {
        return;
      }

      event.preventDefault();

      const targetId = link
        .getAttribute('href')
        .replace('#', '');

      const target = document.getElementById(targetId);

      if (!target) {
        return;
      }

      if (
        window.lenis &&
        typeof window.lenis.scrollTo === 'function'
      ) {
        window.lenis.scrollTo(target, {
          offset: -offset
        });

        return;
      }

      const targetPosition =
        target.getBoundingClientRect().top +
        window.scrollY -
        offset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    });

    root.dataset.tocInitialized = 'true';
  }

  function initializeAllTablesOfContents(scope) {
    const searchScope = scope || document;

    const roots = searchScope.matches?.('[data-toc-wrap]')
      ? [searchScope]
      : Array.from(
          searchScope.querySelectorAll('[data-toc-wrap]')
        );

    roots.forEach((root) => {
      initializeTableOfContents(root);
    });

    if (typeof window.ScrollTrigger !== 'undefined') {
      window.ScrollTrigger.refresh();
    }
  }

  async function startTableOfContents(scope) {
    try {
      await loadDependencies();
      initializeAllTablesOfContents(scope);
    } catch (error) {
      console.error(
        'No fue posible inicializar la tabla de contenidos:',
        error
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        startTableOfContents(document);
      },
      { once: true }
    );
  } else {
    startTableOfContents(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      startTableOfContents(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const roots = event.target.matches?.(
        '[data-toc-wrap]'
      )
        ? [event.target]
        : Array.from(
            event.target.querySelectorAll(
              '[data-toc-wrap]'
            )
          );

      roots.forEach((root) => {
        destroyTableOfContents(root);
      });
    }
  );
})();